import { useCallback, useState } from "react";
import {
  type DeveloperCreditPayload,
  creditDeveloper,
  fetchUserBilling,
  grantTokens,
  replayWebhook,
  resyncEntitlement,
} from "../../api/admin-revenue";
import type { UserBillingData } from "../../api/admin-revenue";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { PageHeader } from "../../components/PageHeader";
import { t } from "../../i18n";
import { AgentCommercePricingPanel } from "./AgentCommercePricingPanel";
import { BillingTabs } from "./BillingTabs";
import { DeveloperCreditDialog } from "./DeveloperCreditDialog";
import { UserBillingPanel } from "./UserBillingPanel";
import type { BillingDialogKind } from "./UserBillingPanel";
import { billingPageStyles as s } from "./billing-page-styles";

type BillingTab = "user-billing" | "agent-commerce";

export function BillingPage(): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<BillingTab>("user-billing");
  const [userId, setUserId] = useState("");
  const [billing, setBilling] = useState<UserBillingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<BillingDialogKind>(null);
  const [developerCreditOpen, setDeveloperCreditOpen] = useState(false);
  const [grantAmount, setGrantAmount] = useState("");
  const [replayTargetId, setReplayTargetId] = useState("");

  const handleSearch = useCallback(async (): Promise<void> => {
    if (!userId.trim()) return;
    setLoading(true);
    setError(null);
    try {
      setBilling(await fetchUserBilling(userId.trim()));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.failed_to_load"));
      setBilling(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const handleConfirm = useCallback(
    async (reason: string): Promise<void> => {
      const targetUserId = userId.trim();
      if (!billing || !targetUserId) return;
      try {
        if (dialog === "grant") {
          await grantTokens(targetUserId, Number(grantAmount), reason);
        } else if (dialog === "resync") {
          await resyncEntitlement(targetUserId, reason);
        } else if (dialog === "replay") {
          await replayWebhook(replayTargetId, reason);
        }
        setDialog(null);
        setBilling(await fetchUserBilling(targetUserId));
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t("common.action_failed"),
        );
        setDialog(null);
      }
    },
    [dialog, billing, userId, grantAmount, replayTargetId],
  );

  const handleDeveloperCreditConfirm = useCallback(
    async (payload: DeveloperCreditPayload): Promise<void> => {
      const targetUserId = userId.trim();
      if (!billing || !targetUserId) return;
      try {
        await creditDeveloper(targetUserId, payload);
        setDeveloperCreditOpen(false);
        setBilling(await fetchUserBilling(targetUserId));
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t("common.action_failed"),
        );
        setDeveloperCreditOpen(false);
      }
    },
    [billing, userId],
  );

  return (
    <div>
      <PageHeader
        title={t("billing.title")}
        description={t("billing.description")}
      />
      <BillingTabs active={activeTab} onChange={setActiveTab} />
      {error && <p style={s.error}>{error}</p>}
      {activeTab === "user-billing" ? (
        <UserBillingPanel
          userId={userId}
          billing={billing}
          loading={loading}
          dialog={dialog}
          grantAmount={grantAmount}
          onSearch={handleSearch}
          onUserIdChange={setUserId}
          onGrantAmountChange={setGrantAmount}
          onDialogChange={setDialog}
          onDeveloperCredit={() => setDeveloperCreditOpen(true)}
          onReplayTarget={setReplayTargetId}
        />
      ) : (
        <AgentCommercePricingPanel />
      )}
      <ConfirmDialog
        open={dialog !== null}
        title={dialogTitle(dialog)}
        description={dialogDesc(dialog, grantAmount)}
        reasonRequired
        onConfirm={handleConfirm}
        onCancel={() => setDialog(null)}
      />
      <DeveloperCreditDialog
        open={developerCreditOpen}
        developerUserId={userId.trim()}
        onConfirm={(payload) => void handleDeveloperCreditConfirm(payload)}
        onCancel={() => setDeveloperCreditOpen(false)}
      />
    </div>
  );
}

/* ---------- Helpers ---------- */

function dialogTitle(kind: BillingDialogKind): string {
  if (kind === "grant") return t("billing.grant_title");
  if (kind === "resync") return t("billing.resync_title");
  if (kind === "replay") return t("billing.replay_title");
  return "";
}

function dialogDesc(kind: BillingDialogKind, amount: string): string {
  if (kind === "grant")
    return t("billing.grant_desc", { amount: amount || "?" });
  if (kind === "resync") return t("billing.resync_desc");
  if (kind === "replay") return t("billing.replay_desc");
  return "";
}
